import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, Clock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getStandaloneDoc } from "@/lib/documents/standaloneDocs";
import { buildDocumentRoute } from "@/lib/documents/documentLinks";
import ProjectShareButton from "@/components/Auth/Projects/ProjectShareButton";

interface StandaloneDocumentViewProps {
  projectId: string;
  project: any;
  documents: any[];
  onConnectToAmp: () => void;
}

const formatDate = (value?: string) => {
  if (!value) return "–";
  try {
    return new Date(value).toLocaleDateString("sv-SE", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "–";
  }
};

/**
 * Avskalad vy när projektet skapades som ett enskilt dokument.
 * Ingen AMP-hub, inga Bas-P/Bas-U-flikar och inga mappar.
 */
const StandaloneDocumentView: React.FC<StandaloneDocumentViewProps> = ({
  projectId,
  project,
  documents,
  onConnectToAmp,
}) => {
  const navigate = useNavigate();
  const def = getStandaloneDoc(project?.primary_document);

  return (
    <div className="flex-1">
      <div className="container max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 -ml-2"
          onClick={() => navigate("/uppdrag")}
        >
          <ArrowLeft className="h-4 w-4" />
          Mina dokument
        </Button>

        <div className="space-y-2">
          {def?.docNumber && (
            <Badge variant="secondary" className="font-normal">
              {def.docNumber}
            </Badge>
          )}
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {project?.name || def?.title || "Dokument"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {def?.what || "Ett enskilt dokument – inget projekt behöver sättas upp."}
          </p>
        </div>

        <Card className="p-4 sm:p-5 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Innehåll
          </h2>

          {documents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Dokumentet är tomt än så länge. Öppna det och börja fylla i.
            </p>
          ) : (
            <ul className="divide-y">
              {documents.map((doc) => (
                <li key={doc.id} className="py-3 flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{doc.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Ändrad {formatDate(doc.updated_at || doc.created_at)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      const route = buildDocumentRoute({
                        projectId,
                        folderId: doc.folder_id,
                        assessmentId: doc.assessment_id,
                        type: doc.type,
                      });
                      if (route) navigate(route);
                    }}
                  >
                    Fortsätt fylla i
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="flex flex-wrap gap-2">
          <ProjectShareButton projectId={projectId} projectName={project?.name || "Dokument"} />
        </div>

        <Card className="p-4 bg-muted/40 border-dashed">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-medium">Vill du koppla det här till en arbetsmiljöplan?</p>
              <p className="text-xs text-muted-foreground">
                Helt frivilligt – dokumentet fungerar lika bra fristående.
              </p>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={onConnectToAmp}>
              Koppla till AMP
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StandaloneDocumentView;
